import axios from 'axios';

const BASE_URL = import.meta.env['VITE_API_URL'] as string | undefined ?? 'http://localhost:3001';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // pour le cookie refresh HttpOnly
  headers: { 'Content-Type': 'application/json' },
});

// Injecte le Bearer token depuis le store (référence lazy pour éviter les imports circulaires)
apiClient.interceptors.request.use((config) => {
  const raw = localStorage.getItem('elbahi-auth');
  if (raw) {
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string } };
    const token = parsed.state?.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Refresh token automatique sur 401
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config;
    const status = error.response?.status;

    // Ne pas retry les routes auth ou si pas de config
    if (!originalRequest || !status || status !== 401 || originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          resolve(apiClient(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      const { data } = await axios.post<{ accessToken: string }>(
        `${BASE_URL}/api/v1/auth/refresh`,
        null,
        { withCredentials: true },
      );

      const { accessToken } = data;

      // Met à jour le store Zustand via localStorage
      const raw = localStorage.getItem('elbahi-auth');
      if (raw) {
        const parsed = JSON.parse(raw) as { state?: object };
        parsed.state = { ...(parsed.state ?? {}), accessToken };
        localStorage.setItem('elbahi-auth', JSON.stringify(parsed));
      }

      onTokenRefreshed(accessToken);
      isRefreshing = false;

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }
      return apiClient(originalRequest);
    } catch {
      isRefreshing = false;
      refreshSubscribers = [];
      // Logout : on vide le store
      localStorage.removeItem('elbahi-auth');
      window.location.href = '/login';
      return Promise.reject(error);
    }
  },
);
